import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  Snackbar,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  SystemUpdateAlt as UpdateIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { applicationsAPI } from '../services/api';

// Компонент заголовка с поиском (без dropdown)
const SearchTableHeader = ({ label, value, onChange, placeholder }) => {
  return (
    <TableCell sx={{ p: 1, minWidth: 120 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
          {label}
        </Typography>
        <TextField
          size="small"
          placeholder={placeholder || 'Поиск...'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete="off"
          InputProps={{
            endAdornment: value ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => onChange('')}
                  sx={{ p: 0.25 }}
                >
                  <ClearIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            '& .MuiInputBase-root': {
              fontSize: '0.75rem',
            },
            '& .MuiInputBase-input': {
              py: 0.5,
              px: 0.5
            }
          }}
        />
      </Box>
    </TableCell>
  );
};

// Компонент карточки приложения для мобильных устройств
const ApplicationCard = ({ app, onEdit, onDelete, onCopy }) => {
  return (
    <Card sx={{ 
      mb: 2, 
      width: '100%',
      maxWidth: '100%',
      overflow: 'hidden'
    }}>
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        {/* Первая строка: название и статус */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1, 
          mb: 1,
          width: '100%'
        }}>
          {/* Название */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body1" sx={{ 
              fontSize: '0.9rem', 
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              mb: 0.25
            }}>
              {app.name}
            </Typography>
            <Typography variant="caption" sx={{ 
              color: 'text.secondary',
              fontSize: '0.75rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block'
            }}>
              {app.description || 'Нет описания'}
            </Typography>
          </Box>

          {/* Статус */}
          <Chip
            label={app.is_active ? 'Активно' : 'Отключено'}
            color={app.is_active ? 'success' : 'error'}
            size="small"
            sx={{ fontSize: '0.7rem' }}
          />
        </Box>

        {/* Вторая строка: Bundle ID */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          mb: 1,
          width: '100%'
        }}>
          <Typography variant="caption" sx={{ 
            color: 'text.secondary',
            fontSize: '0.7rem',
            minWidth: '50px'
          }}>
            Bundle:
          </Typography>
          <Typography variant="body2" sx={{ 
            fontFamily: 'monospace',
            fontSize: '0.75rem',
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {app.bundle}
          </Typography>
          <IconButton
            size="small"
            onClick={() => onCopy(app.bundle)}
            title="Копировать Bundle ID"
            sx={{ p: 0.5 }}
          >
            <CopyIcon sx={{ fontSize: '14px' }} />
          </IconButton>
        </Box>

        {/* Третья строка: API Ключ */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          mb: 1,
          width: '100%'
        }}>
          <Typography variant="caption" sx={{ 
            color: 'text.secondary',
            fontSize: '0.7rem',
            minWidth: '50px'
          }}>
            API:
          </Typography>
          <Typography variant="body2" sx={{ 
            fontFamily: 'monospace',
            fontSize: '0.75rem',
            flex: 1
          }}>
            {app.api_key.substring(0, 12)}...
          </Typography>
          <IconButton
            size="small"
            onClick={() => onCopy(app.api_key)}
            title="Копировать API ключ"
            sx={{ p: 0.5 }}
          >
            <CopyIcon sx={{ fontSize: '14px' }} />
          </IconButton>
        </Box>

        {/* Четвёртая строка: дата создания и управление */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          width: '100%',
          minHeight: '44px'
        }}>
          {/* Дата создания */}
          <Typography variant="caption" sx={{ 
            color: 'text.secondary',
            fontSize: '0.75rem'
          }}>
            Создано: {new Date(app.created_at).toLocaleDateString('ru-RU')}
          </Typography>

          {/* Кнопки управления (touch-friendly) */}
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            <IconButton
              size="medium"
              onClick={() => onEdit(app)}
              title="Редактировать"
              sx={{ 
                minWidth: '44px', 
                minHeight: '44px',
                backgroundColor: 'primary.light',
                color: 'primary.contrastText',
                '&:hover': { backgroundColor: 'primary.main' }
              }}
            >
              <EditIcon sx={{ fontSize: '18px' }} />
            </IconButton>
            <IconButton
              size="medium"
              onClick={() => onDelete(app.id)}
              title="Удалить"
              sx={{ 
                minWidth: '44px', 
                minHeight: '44px',
                backgroundColor: 'error.light',
                color: 'error.contrastText',
                '&:hover': { backgroundColor: 'error.main' }
              }}
            >
              <DeleteIcon sx={{ fontSize: '18px' }} />
            </IconButton>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const ApplicationsPage = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Состояния для фильтрации
  const [nameSearch, setNameSearch] = useState('');
  const [bundleSearch, setBundleSearch] = useState('');

  useEffect(() => {
    loadApplications();
  }, []);

  const loadApplications = async () => {
    try {
      setLoading(true);
      const response = await applicationsAPI.getAll();
      setApplications(response.data);
    } catch (error) {
      console.error('Error loading applications:', error);
      showSnackbar('Ошибка загрузки приложений', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleCreateApplication = () => {
    navigate('/applications/new');
  };

  const handleEditApplication = (app) => {
    navigate(`/applications/${app.id}`);
  };



  const handleDelete = async (id) => {
    if (window.confirm('Вы уверены, что хотите удалить это приложение?')) {
      try {
        await applicationsAPI.delete(id);
        showSnackbar('Приложение удалено');
        loadApplications();
      } catch (error) {
        console.error('Error deleting application:', error);
        showSnackbar('Ошибка удаления', 'error');
      }
    }
  };

  const handleCopyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showSnackbar('Скопировано в буфер обмена');
  };

  // Фильтрация приложений
  const filteredApplications = applications.filter(app => {
    // Фильтр по названию
    if (nameSearch && !app.name?.toLowerCase().includes(nameSearch.toLowerCase())) {
      return false;
    }
    // Фильтр по bundle
    if (bundleSearch && !app.bundle?.toLowerCase().includes(bundleSearch.toLowerCase())) {
      return false;
    }
    return true;
  });

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Загрузка...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Приложения
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<UpdateIcon />}
            onClick={() => navigate('/app-updates')}
          >
            Обновления
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateApplication}
          >
            Добавить приложение
          </Button>
        </Box>
      </Box>

      {/* Адаптивное отображение приложений: карточки на мобильных, таблица на десктопе */}
      {isMobile ? (
        /* МОБИЛЬНАЯ ВЕРСИЯ - КАРТОЧКИ */
        <Box sx={{ 
          width: '100%', 
          maxWidth: '100vw', 
          overflow: 'hidden'
        }}>
          {filteredApplications.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Приложения не найдены
              </Typography>
            </Paper>
          ) : (
            filteredApplications.map((app) => (
              <ApplicationCard
                key={app.id}
                app={app}
                onEdit={handleEditApplication}
                onDelete={handleDelete}
                onCopy={handleCopyToClipboard}
              />
            ))
          )}
        </Box>
      ) : (
        /* ДЕСКТОПНАЯ ВЕРСИЯ - ТАБЛИЦА */
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <SearchTableHeader 
                  label="Название" 
                  value={nameSearch} 
                  onChange={setNameSearch}
                  placeholder="Поиск по названию..."
                />
                <SearchTableHeader 
                  label="Bundle ID" 
                  value={bundleSearch} 
                  onChange={setBundleSearch}
                  placeholder="Поиск по bundle..."
                />
                <TableCell sx={{ p: 1 }}>API Ключ</TableCell>
                <TableCell sx={{ p: 1 }}>Создано</TableCell>
                <TableCell align="right" sx={{ p: 1 }}>Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredApplications.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary">
                      Приложения не найдены
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredApplications.map((app) => (
                  <TableRow key={app.id}>
                    <TableCell sx={{ p: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{app.name}</Typography>
                    </TableCell>
                    <TableCell sx={{ p: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                          {app.bundle}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleCopyToClipboard(app.bundle)}
                          title="Копировать"
                          sx={{ p: 0.5 }}
                        >
                          <CopyIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ p: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                          {app.api_key.substring(0, 8)}...
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleCopyToClipboard(app.api_key)}
                          title="Копировать полный ключ"
                          sx={{ p: 0.5 }}
                        >
                          <CopyIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ p: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {new Date(app.created_at).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ p: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleEditApplication(app)}
                        title="Редактировать"
                        sx={{ p: 0.5 }}
                      >
                        <EditIcon sx={{ fontSize: '1.2rem' }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(app.id)}
                        title="Удалить"
                        color="error"
                        sx={{ p: 0.5 }}
                      >
                        <DeleteIcon sx={{ fontSize: '1.2rem' }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}



      {/* Snackbar для уведомлений */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ApplicationsPage;
